import React from "react";
import { SkeletonLine } from "@ui-components/Skeleton/GenericSkeleton";
import GenericDateTimeRangePicker from "@ui-components/DateTime/GenericDateTimeRangePicker";
import { MaintenanceTableRow } from "../Types";
import { OverviewPanel, PanelLink } from "./OverviewPanel";
import {
  MAINTENANCE_PRESET_LABELS,
  MaintenanceEntry,
  MaintenanceFilter,
  MaintenancePreset,
  MaintenanceTiming,
  capMaintenance,
  parseRecordDate,
} from "./overviewModel";

const PRESETS = Object.keys(MAINTENANCE_PRESET_LABELS) as MaintenancePreset[];

const DEFAULT_SUBTITLE = "Upcoming, in progress and the last 24 hours";

function shortDate(date: Date): string {
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** The subtitle says which span the list covers, so a short list is never read as "that's all". */
function filterSubtitle(filter: MaintenanceFilter): string {
  if (filter.preset === "current") return DEFAULT_SUBTITLE;
  if (filter.preset !== "custom") return MAINTENANCE_PRESET_LABELS[filter.preset];
  if (!filter.start || !filter.end) return "Custom range";
  return `${shortDate(filter.start)} – ${shortDate(filter.end)}`;
}

function presetFilter(preset: MaintenancePreset): MaintenanceFilter {
  return preset === "custom" ? { preset, start: null, end: null } : { preset };
}

/**
 * One pill per preset, the same shape as the service chips above. "Custom"
 * adds the shared range picker under the row; picking a preset again
 * leaves the custom range behind.
 */
function MaintenanceFilterBar({
  filter,
  onChange,
}: {
  filter: MaintenanceFilter;
  onChange: (filter: MaintenanceFilter) => void;
}) {
  return (
    <div className="space-y-2 pb-2">
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Maintenance date range">
        {PRESETS.map((preset) => {
          const selected = filter.preset === preset;
          const tone = selected
            ? "border-semantic-border-brand-primary bg-semantic-bg-brand-secondary text-semantic-text-brand"
            : "border-semantic-border-primary bg-semantic-bg-primary text-semantic-text-secondary hover:bg-semantic-hover-primary";
          return (
            <button
              key={preset}
              type="button"
              aria-pressed={selected}
              onClick={() => !selected && onChange(presetFilter(preset))}
              className={`h-7 rounded-full border px-2.5 text-xs font-semibold transition-colors ${tone}`}
            >
              {MAINTENANCE_PRESET_LABELS[preset]}
            </button>
          );
        })}
      </div>
      {filter.preset === "custom" && (
        <GenericDateTimeRangePicker
          label=""
          dateOnly
          value={{ start: filter.start, end: filter.end }}
          onChange={({ start, end }) => onChange({ preset: "custom", start, end })}
          borderRadiusClass="rounded-md"
        />
      )}
    </div>
  );
}

/** An upcoming window needs no tag: the date leaf already says when. */
const TIMING_TAG: Record<MaintenanceTiming, string | undefined> = {
  upcoming: undefined,
  in_progress: "In progress",
  completed: "Completed",
};

/**
 * A calendar leaf. Fixed square and `self-start`: as a flex child of the row it
 * used to stretch to the text's height, leaving the day stuck at the top of a
 * tall box.
 */
function DateBlock({ date }: { date: Date | null }) {
  if (!date) return <div className="h-12 w-12 shrink-0" />;
  return (
    <div className="flex h-12 w-12 shrink-0 flex-col items-center justify-center self-start rounded-md border border-semantic-border-primary bg-semantic-bg-secondary">
      <span className="text-[10px] font-semibold uppercase leading-tight text-semantic-text-secondary">
        {date.toLocaleDateString(undefined, { month: "short" })}
      </span>
      <span className="font-heading text-lg font-bold leading-none text-semantic-text-primary">
        {date.getDate()}
      </span>
    </div>
  );
}

/** "Tue": the leaf already says the day; the weekday is what it leaves out. */
function weekdayOf(date: Date | null): string {
  return date ? date.toLocaleDateString(undefined, { weekday: "short" }) : "";
}

function MaintenanceRow({
  date,
  description,
  meta,
  tag,
  onOpen,
}: {
  date: Date | null;
  description: string;
  meta: string;
  tag?: string;
  onOpen: () => void;
}) {
  // The notice is one line per part: title, message, window, impact, services.
  // Each keeps its own line and the title leads in bold. Stacked (below `lg`)
  // the notice shows whole, the way the service list beside it does; beside
  // the allocation panel it is cut at three lines and the row opens the rest.
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="-mx-2 flex w-[calc(100%+1rem)] gap-3 rounded-md px-2 py-3 text-left hover:bg-semantic-bg-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-semantic-border-brand"
      >
        <DateBlock date={date} />
        <div className="min-w-0 flex-1">
          <p className="whitespace-pre-line text-sm text-semantic-text-primary first-line:font-semibold lg:line-clamp-3">
            {description}
          </p>
          <p className="mt-1 text-xs text-semantic-text-secondary">
            {tag && (
              <span className="mr-1.5 rounded bg-semantic-bg-brand-secondary px-1.5 py-0.5 font-semibold text-semantic-text-brand">
                {tag}
              </span>
            )}
            {meta}
            <span className="ml-1.5 font-semibold text-semantic-text-brand">Details</span>
          </p>
        </div>
      </button>
    </li>
  );
}

/**
 * What is coming, what is running and what ended in the last day (see
 * `visibleMaintenance`), or every window in the span the selector picks; at
 * most `MAINTENANCE_PANEL_LIMIT` rows, with the count of the rest.
 *
 * The platform window, when there is one, leads: it touches every customer,
 * and its wording comes from `buildPlatformMaintenanceRow` so it reads the same
 * here as in the classic table. It is a notice about now or what comes next,
 * not a record with a date range, so it only shows in the default view.
 */
export function MaintenancePanel({
  entries,
  platformRow,
  filter,
  onFilterChange,
  isLoading,
  onViewAll,
  onOpen,
}: {
  entries: MaintenanceEntry[];
  platformRow: MaintenanceTableRow | null;
  filter: MaintenanceFilter;
  onFilterChange: (filter: MaintenanceFilter) => void;
  isLoading: boolean;
  onViewAll: () => void;
  onOpen: (row: MaintenanceTableRow) => void;
}) {
  const leadRow = filter.preset === "current" ? platformRow : null;
  // Half a custom range filters nothing yet: listing the default view under
  // a "pick the last day" subtitle would read as the answer to a range.
  const isPickingRange = filter.preset === "custom" && (!filter.start || !filter.end);
  const { shown, hiddenCount } = capMaintenance(isPickingRange ? [] : entries, leadRow ? 1 : 0);
  const showMore = !isLoading && hiddenCount > 0;
  return (
    <OverviewPanel
      title="Maintenance"
      subtitle={filterSubtitle(filter)}
      action={<PanelLink label="View all" href="/kontrol/outages" onClick={onViewAll} />}
      footer={
        showMore && (
          <div className="px-5 py-3">
            <PanelLink label={`${hiddenCount} more`} href="/kontrol/outages" onClick={onViewAll} />
          </div>
        )
      }
    >
      <MaintenanceFilterBar filter={filter} onChange={onFilterChange} />
      {isPickingRange ? (
        <p className="py-8 text-center text-sm text-semantic-text-secondary">
          Pick the first and last day of the range.
        </p>
      ) : (
        <MaintenanceBody
          entries={shown}
          platformRow={leadRow}
          isFiltered={filter.preset !== "current"}
          isLoading={isLoading}
          onOpen={onOpen}
        />
      )}
    </OverviewPanel>
  );
}

function MaintenanceBody({
  entries,
  platformRow,
  isFiltered,
  isLoading,
  onOpen,
}: {
  entries: MaintenanceEntry[];
  platformRow: MaintenanceTableRow | null;
  isFiltered: boolean;
  isLoading: boolean;
  onOpen: (row: MaintenanceTableRow) => void;
}) {
  if (isLoading) {
    return (
      <div className="space-y-4 py-2" aria-busy="true">
        {[0, 1, 2].map((index) => (
          <SkeletonLine key={index} h={36} decorative />
        ))}
      </div>
    );
  }
  if (entries.length === 0 && !platformRow) {
    return (
      <p className="py-8 text-center text-sm text-semantic-text-secondary">
        {isFiltered ? "No maintenance in this range." : "No maintenance scheduled."}
      </p>
    );
  }
  return (
    <ul className="divide-y divide-semantic-border-primary">
      {platformRow && (
        <MaintenanceRow
          date={parseRecordDate(platformRow.date)}
          description={platformRow.description}
          meta={[weekdayOf(parseRecordDate(platformRow.date)), platformRow.duration].filter(Boolean).join(" · ")}
          tag="Platform"
          onOpen={() => onOpen(platformRow)}
        />
      )}
      {entries.map((entry) => (
        <MaintenanceRow
          key={entry.key}
          date={entry.date}
          description={entry.description}
          meta={[weekdayOf(entry.date), entry.duration, entry.customer].filter(Boolean).join(" · ")}
          tag={TIMING_TAG[entry.timing]}
          onOpen={() =>
            onOpen({
              date: entry.dateLabel,
              description: entry.description,
              duration: entry.duration,
              customer: entry.customer ?? undefined,
            })
          }
        />
      ))}
    </ul>
  );
}
